import { BusinessObjectBase } from './base';
import type { Token } from './token';

export interface TokenLinkData {
  readonly id: string;
  readonly downstreamTokenId: number;
  readonly downstreamToken?: Token;
  readonly upstreamTokenId: number;
  readonly upstreamToken?: Token;
}

export interface TokenLink extends Readonly<TokenLinkData> {}
export class TokenLink extends BusinessObjectBase {
  static readonly entityName = 'TokenLink' as const;
  static readonly plural = 'token-links';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<TokenLink[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new TokenLink(data));
  }

  static async findOne(filter?: any): Promise<TokenLink | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new TokenLink(data) : null;
  }

  static async findById(id: string, filter?: any): Promise<TokenLink> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new TokenLink(data);
  }

  static async create(data: Omit<TokenLinkData, 'id'>): Promise<TokenLink> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new TokenLink(result);
  }

  static async updateById(
    id: string,
    data: Partial<TokenLinkData>,
  ): Promise<TokenLink> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new TokenLink(result);
  }

  static async deleteById(id: string): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  static async findByUpstream(options: any): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/find-by-upstream`;
    return this.post<any>(url, options);
  }
  static async findByDownstream(options: any): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/find-by-downstream`;
    return this.post<any>(url, options);
  }
}
